import React, { useState, useEffect } from 'react';
import { MapPin, Search, RefreshCw, Warehouse, Filter, X, Package } from 'lucide-react';
import { dataInspectionService } from '../../../../../services/dataInspectionService';
import { StcBinDto } from '../../../../../types/dataInspection';
import { BackButton } from '../../../../common/BackButton';

interface StcBinsViewerProps {
    onBack: () => void;
}

export const StcBinsViewer: React.FC<StcBinsViewerProps> = ({ onBack }) => {
    const [stcBins, setStcBins] = useState<StcBinDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState<'all' | 'warehouse' | 'zone'>('all');
    const [showFilters, setShowFilters] = useState(false);

    useEffect(() => {
        loadStcBins();
    }, []);

    const loadStcBins = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await dataInspectionService.getAllStcBins();
            setStcBins(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'En feil oppstod');
        } finally {
            setLoading(false);
        }
    };

    const searchByWarehouse = async (warehouseID: string) => {
        try {
            setLoading(true);
            setError('');
            const data = await dataInspectionService.getStcBinsByWarehouse(warehouseID);
            setStcBins(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'En feil oppstod');
        } finally {
            setLoading(false);
        }
    };

    const searchByZone = async (zoneCode: string) => {
        try {
            setLoading(true);
            setError('');
            const data = await dataInspectionService.getStcBinsByZone(zoneCode);
            setStcBins(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'En feil oppstod');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        if (!searchTerm.trim()) {
            loadStcBins();
            return;
        }

        switch (filterType) {
            case 'warehouse':
                searchByWarehouse(searchTerm);
                break;
            case 'zone':
                searchByZone(searchTerm);
                break;
            default:
                // For 'all', just filter locally
                break;
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const clearFilters = () => {
        setSearchTerm('');
        setFilterType('all');
        loadStcBins();
    };

    const filteredStcBins = filterType === 'all' ? stcBins.filter(bin => 
        bin.binCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bin.warehouseID.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bin.description1?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bin.zoneCode?.toLowerCase().includes(searchTerm.toLowerCase())
    ) : stcBins;

    const getStatusColor = (bin: StcBinDto) => {
        if (bin.binBlocked) return 'bg-red-100 text-red-800';
        if (bin.binEmpty) return 'bg-gray-100 text-gray-800';
        if (bin.binFixed) return 'bg-blue-100 text-blue-800';
        return 'bg-green-100 text-green-800';
    };

    const getStatusText = (bin: StcBinDto) => {
        if (bin.binBlocked) return 'Blokkert';
        if (bin.binEmpty) return 'Tom';
        if (bin.binFixed) return 'Fast';
        return 'Aktiv';
    };

    const getBinTypeColor = (binType: string | undefined) => {
        switch (binType?.toUpperCase()) {
            case 'PLUKKPLASSERING': return 'bg-green-100 text-green-800';
            case 'BULKPLASSERING': return 'bg-blue-100 text-blue-800';
            case 'MOTTAK': return 'bg-purple-100 text-purple-800';
            case 'EKSPORT': return 'bg-orange-100 text-orange-800';
            case 'KTING': return 'bg-yellow-100 text-yellow-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const totalBins = filteredStcBins.length;
    const activeBins = filteredStcBins.filter(bin => !bin.binBlocked && !bin.binEmpty).length;
    const blockedBins = filteredStcBins.filter(bin => bin.binBlocked).length;
    const emptyBins = filteredStcBins.filter(bin => bin.binEmpty).length;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                <BackButton
                    onClick={onBack}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
                    iconSize={16}
                    label="Tilbake til grunndata"
                />
                <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-red-600" />
                    <h1 className="text-xl font-semibold">Lokasjoner</h1>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-500">Totalt</p>
                            <p className="text-2xl font-bold text-gray-900">{totalBins}</p>
                        </div>
                        <MapPin className="w-8 h-8 text-gray-500" />
                    </div>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-500">Aktive</p>
                            <p className="text-2xl font-bold text-green-600">{activeBins}</p>
                        </div>
                        <Package className="w-8 h-8 text-green-500" />
                    </div>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-500">Blokkerte</p>
                            <p className="text-2xl font-bold text-red-600">{blockedBins}</p>
                        </div>
                        <X className="w-8 h-8 text-red-500" />
                    </div>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-500">Tomme</p>
                            <p className="text-2xl font-bold text-gray-600">{emptyBins}</p>
                        </div>
                        <Package className="w-8 h-8 text-gray-400" />
                    </div>
                </div>
            </div>

            {/* Search and Controls */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                        <div className="flex items-center gap-4 flex-1">
                            <div className="relative flex-1 max-w-md">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <input
                                    type="text"
                                    placeholder="Søk etter lokasjon, lager eller sone..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                <Filter className="w-4 h-4" />
                                Filter
                            </button>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={handleSearch}
                                disabled={loading}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                            >
                                <Search className="w-4 h-4" />
                                Søk
                            </button>
                            <button
                                onClick={loadStcBins}
                                disabled={loading}
                                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                            >
                                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                                Alle
                            </button>
                        </div>
                    </div>

                    {/* Filter Options */}
                    {showFilters && (
                        <div className="border-t pt-4">
                            <div className="flex items-center gap-4">
                                <span className="text-sm font-medium text-gray-700">Søketype:</span>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setFilterType('all')}
                                        className={`px-3 py-1 rounded-full text-sm ${filterType === 'all' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}
                                    >
                                        Alle
                                    </button>
                                    <button
                                        onClick={() => setFilterType('warehouse')}
                                        className={`px-3 py-1 rounded-full text-sm ${filterType === 'warehouse' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}
                                    >
                                        Bare lager
                                    </button>
                                    <button
                                        onClick={() => setFilterType('zone')}
                                        className={`px-3 py-1 rounded-full text-sm ${filterType === 'zone' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}
                                    >
                                        Bare sone
                                    </button>
                                </div>
                                <button
                                    onClick={clearFilters}
                                    className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                                >
                                    <X className="w-3 h-3" />
                                    Nullstill
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-red-700">
                        <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                        <span className="font-medium">Feil:</span>
                    </div>
                    <p className="text-red-600 mt-1">{error}</p>
                </div>
            )}

            {/* StcBins Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Lokasjoner ({filteredStcBins.length} stk)</h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lokasjon</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Beskrivelse</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lager</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sone</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kapasitet</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dimensjoner</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Egenskaper</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Oppdatert</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredStcBins.map(bin => (
                                <tr key={bin.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        <div className="flex items-center gap-2">
                                            <MapPin className="w-4 h-4 text-gray-400" />
                                            {bin.binCode}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                                        {bin.description1 || 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <div className="flex items-center gap-2">
                                            <Warehouse className="w-4 h-4 text-gray-400" />
                                            {bin.warehouseID}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {bin.zoneCode || 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getBinTypeColor(bin.binType)}`}>
                                            {bin.binType || 'N/A'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(bin)}`}>
                                            {getStatusText(bin)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {bin.binCapacity || 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {bin.binLength && bin.binWidth && bin.binHeight ? 
                                            `${bin.binLength}×${bin.binWidth}×${bin.binHeight}` : 
                                            'N/A'
                                        }
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <div className="flex flex-wrap gap-1">
                                            {bin.binPickable && <span className="inline-flex px-1 py-0.5 rounded text-xs bg-green-100 text-green-800">Plukk</span>}
                                            {bin.binReceivable && <span className="inline-flex px-1 py-0.5 rounded text-xs bg-blue-100 text-blue-800">Mottak</span>}
                                            {bin.binCountable && <span className="inline-flex px-1 py-0.5 rounded text-xs bg-purple-100 text-purple-800">Telling</span>}
                                            {bin.binReplenishable && <span className="inline-flex px-1 py-0.5 rounded text-xs bg-orange-100 text-orange-800">Etterfyll</span>}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {bin.lastUpdate ? new Date(bin.lastUpdate).toLocaleDateString('nb-NO') : 'N/A'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};